/**
 * Second line of defense for OOXML merge recovery. The first line —
 * backend `OOXMLFixer` via `/redline/repair-merge/stream` — runs inside
 * `applyOoxmlChanges`. When that exhausts its budget the agent
 * fallback exposed here diffs CURRENT vs TARGET and applies the
 * tracked changes via `execute_js`, under the allowance that
 * `createJsExecutionBudget` defines. Verification is deterministic and
 * runs in `recovery-verification.ts`.
 */

import { useCallback } from "react";
import type { AgentActionOptions } from "./agent-action";
import { OoxmlMergeError } from "./ooxml-merge";
import {
    withWordContextWrite,
    getExpandedRangeForBookmarks,
    applyRedlineOoxml,
    applyBulkMergeData,
    type ApplyBulkMergeDataResult,
    ApplyRedlineOoxmlArgs,
    RunAgentRecoveryArgs,
    BulkApplyProgress,
    type PackageOrigin,
} from "./document";
import type { BulkApplyMergeEntry } from "./bulk-apply-merge-entry";
import type { RedlineApplySource } from "./redline-apply-outcome";
import type { RedlineAttemptRecorder } from "./redline-apply-recorder";
import { verifyRecovery, describeFirstDivergence, extractBodyContent, redlineSignature, RecoveryOutcome, RedlineSignature } from "./recovery-verification";
import { normalizeWordErrorFields } from "./redline-failure";
import type { RedlineApplyContext } from "./redline-snapshot";
import { useChat } from "../context/ChatContext";
import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import { ApplyFailureError, CANNOT_LOCATE_TARGET_MESSAGE, UserFacingError } from "./errors";

/** Tooltip shown next to "Applied" when post-hoc verification reports drift. */
export const RECOVERY_USED_MESSAGE =
    "We applied this redline through an automatic recovery fallback and " +
    "the result doesn't exactly match what was expected. Please double-check.";

/** Sonnet 4.6 is the only model that ships a correct first-attempt
 *  script reliably; Haiku spirals on ambiguous `range.search` matches. */
const RECOVERY_MODEL = "claude-sonnet-4-6";

interface WordMergeErrorInfo {
    code?: string;
    message?: string;
    location?: string;
}

interface BuildRecoveryPromptArgs {
    currentBody: string;
    targetBody: string;
    bookmarks: string[];
    mergeStep: string;
    wordError?: WordMergeErrorInfo;
}

/** User-facing recovery prompt. Script-writing rules (anchor selection,
 *  two-phase pattern, banned ops) live in the agent system prompt's
 *  `<javascript_tracked_changes_guide>`; this carries only the task,
 *  the OOXML payload, and the one-script policy. */
export function buildRecoveryPrompt({
    currentBody,
    targetBody,
    bookmarks,
    mergeStep,
    wordError,
}: BuildRecoveryPromptArgs): string {
    const wordErrorBlock = wordError && (wordError.code || wordError.message || wordError.location)
        ? [
            "Word.js threw the following error during the failed merge:",
            wordError.code ? `  code: ${wordError.code}` : null,
            wordError.message ? `  message: ${wordError.message}` : null,
            wordError.location ? `  location: ${wordError.location}` : null,
        ].filter(Boolean).join("\n")
        : "Word.js did not surface a structured error code.";

    const bookmarksBlock = bookmarks.length > 0
        ? bookmarks.map(name => `  - ${name}`).join("\n")
        : "  (none captured — the failed merge was selection-based; locate the target paragraph by matching CURRENT against the document body via `request_focused_document`)";

    const lookupExample = bookmarks.length > 0
        ? `const bookmarkRange = context.document.getBookmarkRangeOrNullObject(${JSON.stringify(bookmarks[0])});
bookmarkRange.load("isNullObject");
await context.sync();
if (bookmarkRange.isNullObject) {
    console.log("ERROR: bookmark ${bookmarks[0]} not found");
    return;
}
const paragraph = bookmarkRange.paragraphs.getFirst();`
        : `// No bookmark captured — call request_focused_document to fetch the body and
// match against CURRENT to identify the paragraph, then anchor your searches on
// text from CURRENT.`;

    return `A redline merge into the user's open Word document failed at the local OOXML stage. Recover by applying the diff via tracked changes through \`execute_js\`.

Failed merge step: ${mergeStep}
${wordErrorBlock}

Target paragraph(s) by bookmark name:
${bookmarksBlock}

Below are CURRENT (the live document state right before the failed merge) and TARGET (the desired state). Both are inner \`<w:body>\` XML. Some \`<w:ins>\` / \`<w:del>\` runs in TARGET may already be present in CURRENT (from a prior partial application, manual edits, or earlier redlines). Apply ONLY the runs that are in TARGET but NOT in CURRENT. Existing tracked changes in CURRENT must be left untouched.

CURRENT:
\`\`\`xml
${currentBody}
\`\`\`

TARGET:
\`\`\`xml
${targetBody}
\`\`\`

How to write the script: follow \`<javascript_tracked_changes_guide>\` in your system prompt. The two-phase pattern (search all, then mutate all), the anchor rule (every search must match text that's in CURRENT — including \`<w:del>\` text, but NEVER text from a TARGET-only \`<w:ins>\`), the no-collapse rule for reading OOXML, and the bans on \`.accept()\` / \`.reject()\` / \`insertOoxml\` all apply here.

Find the target paragraph via \`context.document.getBookmarkRangeOrNullObject(name)\`. The bookmark names above are the same paragraph bookmarks (\`_dioptra_p_*\`) used to insert this redline originally — they persist across the failed merge. Do NOT use \`uniqueLocalId\`: Word can refresh it as a side-effect of the failed \`insertOoxml\`, leaving any pre-captured ids stale.

Lookup pattern:
\`\`\`javascript
${lookupExample}
\`\`\`

If multiple bookmark names are listed, look them up the same way and union their ranges with \`firstRange.expandTo(secondRange)\` before reading paragraphs.

STRICT one-change policy. You get exactly ONE \`execute_js\` call that changes the document: once a script of yours has edited it, the addin REJECTS every later call before it runs, because each silent re-apply leaves duplicate tracked changes. A script that changed nothing does not spend that one change — so when a phase-1 search returns 0 or >1 matches even after one compound-anchor expansion, you may follow it with one more script that reads \`paragraph.text\` to find the anchor you missed, then apply. You get ${MAX_RECOVERY_JS_DISPATCHES} \`execute_js\` calls in total; when they are spent, stop and tell the user in one sentence what didn't work, and the user re-runs the original merge from a clean state.

Do NOT include verification logic in the script. Do not count tracked changes before/after. Do not read \`paragraph.text\` to confirm a change you just made. Do not log \`VERIFICATION_PASSED\` or \`VERIFICATION_FAILED\`. The script's job is to apply the edits and return. Output overrides for this recovery flow (these REPLACE the corresponding parts of \`<javascript_execution>\`): no per-step \`console.log\` and no \`EDIT_SUMMARY\` block (skip PART 3 entirely). Reply in one short sentence after the script completes. The general "ZERO COMMENTS" rule from \`<javascript_execution>\` applies as written — do not narrate the script inline. Do not call any tool other than \`execute_js\` or \`request_focused_document\`.`;
}

type RunAgentActionFn = (
    prompt: string,
    toolNames: string[],
    modelName?: string,
    agentActionOptions?: AgentActionOptions,
) => Promise<string>;

/** Ceiling on `execute_js` dispatches in one recovery run.
 *
 *  WHY: `createJsExecutionBudget` stops a second CHANGING script but never
 *  stops a model that keeps emitting scripts which change nothing — without
 *  this it runs to LangGraph's `recursion_limit` of 200, at up to one
 *  30-second backend poll per call.
 *  CASES: the salvageable run is three dispatches — an attempt whose anchor
 *  search misses, a read-back of `paragraph.text` to find the real anchor,
 *  and the retry that applies the edit.
 */
export const MAX_RECOVERY_JS_DISPATCHES = 3;

const DOCUMENT_ALREADY_CHANGED_REASON =
    "RECOVERY_FLOW_TERMINATED: one of your scripts has already changed the document. " +
    "A second pass would duplicate those tracked changes. Stop and tell the user in one " +
    "sentence what you applied.";

const DISPATCHES_SPENT_REASON =
    `RECOVERY_FLOW_TERMINATED: this flow allows ${MAX_RECOVERY_JS_DISPATCHES} execute_js calls and ` +
    "they are spent. Stop and tell the user in one sentence what you could not do.";

interface JsExecutionBudget {
    /** RETURNS: null while the next `execute_js` may run; otherwise the tool-error
     *  text the agent receives in place of running it. */
    rejectionReasonForNextScript: () => string | null;
    /** WHEN: a script has finished, before its result reaches the backend — so the
     *  next request is judged against the document the script left behind. */
    onScriptFinished: () => Promise<void>;
}

/** Snapshot the merged-target range's body.
 *
 *  NULL: no bookmarks were captured, or they no longer resolve. Upstream
 *  verification treats that as drift; the budget treats it as "cannot prove the
 *  script left the document alone". */
async function snapshotBookmarkedBody(targetParagraphBookmarks: string[]): Promise<string | null> {
    if (targetParagraphBookmarks.length === 0) return null;
    let ooxml: string | null = null;
    await withWordContextWrite(async (context) => {
        const range = await getExpandedRangeForBookmarks(context, targetParagraphBookmarks);
        if (!range) return;
        const result = range.getOoxml();
        await context.sync();
        ooxml = extractBodyContent(result.value);
    });
    return ooxml;
}

/** Tracked-change fingerprint of the target range right now.
 *
 *  WHY: every edit this flow is allowed to make is a tracked insertion or
 *  deletion, so two equal signatures mean no script touched the range.
 *  NULL: the range could not be read — see `snapshotBookmarkedBody`. */
async function redlineSignatureOfBookmarkedRange(targetParagraphBookmarks: string[]): Promise<RedlineSignature | null> {
    const bodyXml = await snapshotBookmarkedBody(targetParagraphBookmarks);
    return bodyXml === null ? null : redlineSignature(bodyXml);
}

/** The run's allowance of `execute_js` calls, counted in document changes.
 *
 *  WHY: one script may CHANGE the target range, because a second editing pass
 *  duplicates the tracked changes it already wrote. The guard this replaces
 *  counted DISPATCHES instead, so a script that crashed or self-aborted
 *  without editing anything spent the whole allowance and the retry that would
 *  have worked was refused.
 *  EXAMPLE: script 1 logs `ERROR: anchor ' REF _Ref238670905' not found` and
 *           returns    -> signature unchanged  -> next script allowed
 *           script 2 reads `paragraph.text`    -> signature unchanged  -> allowed
 *           script 3 inserts a `<w:ins>`       -> signature changed    -> refused
 */
async function createJsExecutionBudget(targetParagraphBookmarks: string[]): Promise<JsExecutionBudget> {
    // WHEN Word refuses the baseline read — the failed `insertOoxml` this flow
    // recovers from is exactly when it leaves the document unreadable.
    // CONSEQUENCE: no baseline, so the run takes the NULL fallback below and
    // spends itself after one script. Recovery still happens; letting the
    // rejection out would deny the agent the attempt this flow exists to give.
    let signatureBeforeLastScript = await redlineSignatureOfBookmarkedRange(targetParagraphBookmarks).catch(() => null);
    let scriptsDispatched = 0;
    let aScriptHasChangedTheDocument = false;

    /** WHEN a script has run to completion, whatever it did or failed to do.
     *
     *  CONSEQUENCE: the dispatch is counted and the range is re-read, so the
     *  next `rejectionReasonForNextScript` judges the document this script left
     *  behind rather than the one it started from. */
    async function onScriptFinished(): Promise<void> {
        // BOUNDARY: counts scripts that have RUN, not scripts admitted, so the
        // count lands one below the ceiling while a script is still allowed.
        // EXAMPLE: 0 -> 1 after the anchor-miss script, 1 -> 2 after the
        //          read-back, 2 -> 3 after the retry, and 3 refuses the next.
        scriptsDispatched++;

        let signatureAfterScript: RedlineSignature | null;
        try {
            signatureAfterScript = await redlineSignatureOfBookmarkedRange(targetParagraphBookmarks);
        } catch (rangeReadError) {
            // WHEN Word refuses the post-script read — a `GeneralException` on
            // the write lock, or bookmarks the script itself destroyed.
            // CONSEQUENCE: the flow cannot prove the script left the range
            // alone, so it closes the same way the NULL case below does and
            // lets the caller see why the read failed.
            aScriptHasChangedTheDocument = true;
            throw rangeReadError;
        }

        // NULL: either side missing means the range is unreadable, so the flow
        // cannot prove the script left it alone. FALLBACK: assume it changed —
        // one dispatch total, which is the rule this flow shipped with.
        if (signatureBeforeLastScript === null || signatureAfterScript === null) {
            aScriptHasChangedTheDocument = true;
            return;
        }

        // WHY stringify: `RedlineSignature` is a flat record of numbers and
        // strings that both sides get from the one object literal in
        // `redlineSignature`, so key order matches and any field added there
        // is compared without this line having to learn about it.
        if (JSON.stringify(signatureBeforeLastScript) !== JSON.stringify(signatureAfterScript)) {
            aScriptHasChangedTheDocument = true;
        }
        signatureBeforeLastScript = signatureAfterScript;
    }

    /** WHEN the stream is about to hand the add-in another `execute_js` script.
     *
     *  RETURNS: null while the script may run; otherwise the tool-error text
     *  that runs in its place, naming which allowance ran out. */
    function rejectionReasonForNextScript(): string | null {
        if (aScriptHasChangedTheDocument) return DOCUMENT_ALREADY_CHANGED_REASON;
        if (scriptsDispatched >= MAX_RECOVERY_JS_DISPATCHES) return DISPATCHES_SPENT_REASON;
        return null;
    }

    return { rejectionReasonForNextScript, onScriptFinished };
}

/**
 * Agent-driven OOXML recovery for a failed `applyOoxmlChanges`. Must be
 * called *outside* the write lock that produced the error — the agent's
 * tools and the post-hoc snapshot both acquire their own locks. Returns
 * the verification outcome; throws `UserFacingError` on hard agent or
 * network failure. Drift never throws — the document is already mutated
 * and hiding that would make things worse.
 */
export async function runAgentRecovery({
    error,
    applyContext,
    recorder,
    runAgentAction,
}: RunAgentRecoveryArgs & { runAgentAction: RunAgentActionFn }): Promise<RecoveryOutcome> {
    const targetBody = extractBodyContent(error.failedOoxml);
    const bookmarks = error.bookmarks || [];

    const attemptedPair = {
        attemptedOoxml: error.failedOoxml,
        currentRangeOoxml: error.currentRangeOoxml,
    };

    try {
        const prompt = buildRecoveryPrompt({
            currentBody: extractBodyContent(error.currentRangeOoxml),
            targetBody,
            bookmarks,
            mergeStep: error.mergeStep,
            wordError: error.wordError,
        });

        const jsExecutionBudget = await createJsExecutionBudget(bookmarks);

        await runAgentAction(
            prompt,
            ["execute_js", "request_focused_document"],
            RECOVERY_MODEL,
            {
                onScriptFinished: jsExecutionBudget.onScriptFinished,
                rejectJsExecutionReason: jsExecutionBudget.rejectionReasonForNextScript,
            },
        );

        if (bookmarks.length === 0) {
            console.info(
                `[OOXML_RECOVERY] no bookmarks captured — skipping verification ` +
                `(merge_step=${error.mergeStep})`,
            );
            return { status: "clean", distance: 0 };
        }
    } catch (recoverErr) {
        console.error('[OOXML_MERGE_RECOVERY_FAILURE] Agent recovery failed.', {
            mergeStep: error.mergeStep,
            recoverErr: recoverErr instanceof Error ? recoverErr.message : String(recoverErr),
        });
        const agentFailure = recoverErr instanceof Error ? recoverErr : new Error(String(recoverErr));
        recorder.recordFailureAndKeepItsSnapshotId({
            applyContext,
            failure: {
                stage: "agent_recovery_threw",
                bookmarks,
                ooxml: attemptedPair,
                wordError: normalizeWordErrorFields(error.wordError),
                agentFailureMessage: agentFailure.message,
            },
            error: agentFailure,
        });
        throw new UserFacingError(
            "Failed to apply changes to the document. The OOXML may be incompatible.",
            agentFailure,
        );
    }

    let actualBody: string | null = null;
    try {
        actualBody = await snapshotBookmarkedBody(bookmarks);
    } catch (snapshotErr) {
        console.error('[OOXML_MERGE_RECOVERY_SNAPSHOT_FAILURE]', snapshotErr);
    }

    const outcome = verifyRecovery({ targetBody, actualBody });

    if (outcome.status === "clean") {
        console.info(
            `[OOXML_RECOVERY] clean — text matches TARGET ` +
            `(distance=${outcome.distance}, bookmarks=${bookmarks.join(",")})`,
        );
    } else {
        const divergence = describeFirstDivergence(outcome.expectedText, outcome.actualText);
        console.warn(
            `[OOXML_RECOVERY_DRIFT] ${outcome.reason}\n` +
            `  bookmarks: ${bookmarks.join(",") || "(none)"}\n` +
            `  merge_step: ${error.mergeStep}\n` +
            `  expected (len=${outcome.expectedText.length}): ${JSON.stringify(outcome.expectedText)}\n` +
            `  actual   (len=${outcome.actualText.length}): ${JSON.stringify(outcome.actualText)}\n` +
            `  ${divergence ?? "(strings identical — divergence reported by snapshot-failure branch)"}`,
        );

        const firstDivergenceOffset = (expected: string, actual: string): number | null => {
            if (expected === actual) return null;
            let index = 0;
            const shared = Math.min(expected.length, actual.length);
            while (index < shared && expected.charCodeAt(index) === actual.charCodeAt(index)) index++;
            return index;
        };

        recorder.recordFailureAndKeepItsSnapshotId({
            applyContext,
            failure: {
                stage: "agent_recovery_drifted",
                bookmarks,
                ooxml: attemptedPair,
                wordError: normalizeWordErrorFields(error.wordError),
                drift: {
                    distance: outcome.distance,
                    reason: outcome.reason,
                    expectedText: outcome.expectedText,
                    actualText: outcome.actualText,
                    divergenceOffset: firstDivergenceOffset(outcome.expectedText, outcome.actualText),
                },
            },
            error: new Error(`OOXML recovery drift: ${outcome.reason}`),
        });
    }

    return outcome;
}

/** Internal — bound to the current ChatContext. Threaded into the
 *  `useApply*` hooks below so call sites never see this primitive. */
function useAgentRecovery(): (args: RunAgentRecoveryArgs) => Promise<RecoveryOutcome> {
    const { runAgentAction } = useChat();
    return useCallback(
        ({ error, applyContext, recorder }: RunAgentRecoveryArgs) =>
            runAgentRecovery({ error, applyContext, recorder, runAgentAction }),
        [runAgentAction],
    );
}

/** Caller-facing arg shape — `runAgentRecovery` is stripped (injected internally). */
export type UseApplyRedlineArgs = Omit<ApplyRedlineOoxmlArgs, 'runAgentRecovery'>;

/** Hook: apply a single redline OOXML with backend repair + agent
 *  fallback baked in. Lifecycle reported via `args.onStatus`
 *  (`in_progress` / `success` / `warning` / `failed`). On `failed`
 *  also throws `UserFacingError`. */
export function useApplyRedlineOoxml(): (args: UseApplyRedlineArgs) => Promise<boolean> {
    const runAgentRecoveryCb = useAgentRecovery();
    return useCallback(
        (args) => applyRedlineOoxml({ ...args, runAgentRecovery: runAgentRecoveryCb }),
        [runAgentRecoveryCb],
    );
}

export interface UseApplyBulkMergeArgs {
    mergeData: BulkApplyMergeEntry[];
    applySource: RedlineApplySource;
    sendError?: (error: any, context?: any) => void;
    /** "pipeline" for the server pipeline's markup, "engine" for packages the add-in's engine composed. */
    origin: PackageOrigin;
    /** `true` while any entry in the batch is currently repairing. */
    onRepairing?: (inProgress: boolean) => void;
    onProgress?: (progress: BulkApplyProgress) => void;
}

/** Hook: bulk-apply variant of `useApplyRedlineOoxml`. Wires the
 *  agent fallback into each per-entry call internally. */
export function useApplyBulkMergeData(): (args: UseApplyBulkMergeArgs) => Promise<ApplyBulkMergeDataResult> {
    const runAgentRecoveryCb = useAgentRecovery();
    return useCallback(
        ({ mergeData, applySource, sendError, origin, onRepairing, onProgress }) =>
            applyBulkMergeData({
                mergeData,
                applySource,
                sendError,
                repairOptions: {
                    origin,
                    runAgentRecovery: runAgentRecoveryCb,
                    onRepairing,
                    onProgress,
                },
            }),
        [runAgentRecoveryCb],
    );
}
